/**
 * ShareButtons — One-line action row for sharing any page / vendor / package
 * to WhatsApp, Facebook, X, LinkedIn, or copying the link to clipboard.
 *
 * No API required — all platforms use a URL-scheme based share intent.
 *
 * Props:
 *   - url     (string)   full https URL to share (defaults to window.location.href)
 *   - text    (string)   short share message
 *   - hashtags (string?) comma-separated, used by X
 *   - compact (boolean)  icon-only mode for tight layouts
 */
import React, { useState } from 'react';
import { WhatsappLogo, FacebookLogo, XLogo, LinkedinLogo, LinkSimple, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

const enc = (s) => encodeURIComponent(s || '');

const ShareButtons = ({ url, text = '', hashtags = '', compact = false }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const message = text || (typeof document !== 'undefined' ? document.title : '');

  const whatsappHref = `https://wa.me/?text=${enc(`${message} ${shareUrl}`)}`;
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`;
  const xHref = `https://twitter.com/intent/tweet?url=${enc(shareUrl)}&text=${enc(message)}${hashtags ? `&hashtags=${enc(hashtags)}` : ''}`;
  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy — try selecting the URL manually');
    }
  };

  const Btn = ({ href, onClick, label, color, Icon, testid }) => (
    <a
      href={href}
      onClick={onClick}
      target={href ? '_blank' : undefined}
      rel={href ? 'noopener noreferrer' : undefined}
      className={`inline-flex items-center justify-center gap-1.5 px-3 h-9 rounded-full border border-[#E8E1D5] text-sm font-medium transition hover:scale-[1.04] ${color}`}
      data-testid={testid}
      aria-label={label}
      title={label}
    >
      <Icon size={16} weight="fill" />
      {!compact && <span>{label}</span>}
    </a>
  );

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-buttons">
      <Btn href={whatsappHref} label="WhatsApp" color="hover:bg-green-50 text-green-700"
        Icon={WhatsappLogo} testid="share-whatsapp" />
      <Btn href={facebookHref} label="Facebook" color="hover:bg-blue-50 text-blue-700"
        Icon={FacebookLogo} testid="share-facebook" />
      <Btn href={xHref} label="X / Twitter" color="hover:bg-stone-100 text-stone-800"
        Icon={XLogo} testid="share-x" />
      <Btn href={linkedinHref} label="LinkedIn" color="hover:bg-sky-50 text-sky-700"
        Icon={LinkedinLogo} testid="share-linkedin" />
      <button
        type="button"
        onClick={copy}
        className={`inline-flex items-center justify-center gap-1.5 px-3 h-9 rounded-full border border-[#E8E1D5] text-sm font-medium transition hover:scale-[1.04] ${copied ? 'bg-green-50 text-green-700 border-green-300' : 'hover:bg-stone-50 text-stone-800'}`}
        data-testid="share-copy"
        aria-label="Copy link"
        title="Copy link"
      >
        {copied ? <Check size={16} weight="bold" /> : <LinkSimple size={16} />}
        {!compact && <span>{copied ? 'Copied' : 'Copy link'}</span>}
      </button>
    </div>
  );
};

export default ShareButtons;
